// src/services/offlineDb.js
//
// The IndexedDB layer behind offline mode (this feature's own plan doc,
// "Real Offline Mode for the Cashier/Checkout Screen") — everything that
// keeps the Checkout screen usable with zero connectivity lives here, in
// three object stores:
//
//  - "queue": one row per mutation queued while offline, in the exact order
//    it must replay in (insertion order == dependency order — see
//    offlineSync.js's own doc comment for why that alone is enough, with no
//    separate dependency graph needed). Shape:
//      { id (autoIncrement), type: "open_shift" | "sale" | "close_shift",
//        clientRequestId (uuid), payload, createdAt (ISO string),
//        status: "pending" | "syncing" | "failed",
//        error?: string,
//        dependsOnShiftClientRequestId?: string | null }
//  - "cache": read-only snapshots needed to keep selling offline, keyed by a
//    fixed string id per snapshot kind ("products", "categories",
//    "customers", "currencies", "services", "sections", "tables",
//    "currentShift"). Shape: { key, data, cachedAt }.
//  - "meta": small singleton rows keyed the same way — "offline_since" (ISO
//    timestamp of the FIRST queued-while-offline mutation this offline
//    stretch, reset to null once a sync fully drains the queue) and
//    "lastSyncedAt".
//
// idb (https://github.com/jakearchibald/idb) is used purely as a thin
// Promise wrapper around the raw IndexedDB API — no schema magic beyond
// what upgrade() below declares.
import { openDB } from "idb";

const DB_NAME = "posflect-offline";
const DB_VERSION = 1;

let dbPromise = null;

export function getOfflineDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        db.createObjectStore("queue", { keyPath: "id", autoIncrement: true });
        db.createObjectStore("cache", { keyPath: "key" });
        db.createObjectStore("meta", { keyPath: "key" });
      },
    });
  }
  return dbPromise;
}

// --- queue ------------------------------------------------------------

// enqueue records one mutation and returns its assigned id. Also stamps
// meta.offline_since the FIRST time anything is queued during a given
// offline stretch (see markSyncComplete below for where that gets cleared).
//
// dependsOnShiftClientRequestId/shiftServerId are only meaningful for a
// "close_shift" item, and are mutually exclusive (see offlineSync.js's own
// doc comment): dependsOnShiftClientRequestId names the "open_shift" queue
// item this close depends on when THAT shift was ALSO opened offline and
// hasn't synced yet (its real, server-assigned id isn't known until that
// item replays); shiftServerId is the real id directly, already known at
// enqueue time, when the shift being closed was opened online (a plain
// numeric id, not a fabricated "offline-<uuid>" one).
export async function enqueueMutation({
  type,
  clientRequestId,
  payload,
  dependsOnShiftClientRequestId = null,
  shiftServerId = null,
}) {
  const db = await getOfflineDb();
  const row = {
    type,
    clientRequestId,
    payload,
    createdAt: new Date().toISOString(),
    status: "pending",
    dependsOnShiftClientRequestId,
    shiftServerId,
  };
  const id = await db.add("queue", row);

  const existing = await db.get("meta", "offline_since");
  if (!existing) {
    await db.put("meta", { key: "offline_since", value: row.createdAt });
  }

  return id;
}

export async function getQueueItems() {
  const db = await getOfflineDb();
  const items = await db.getAll("queue");
  // IndexedDB's autoIncrement keys are already monotonic, but sort
  // explicitly anyway — insertion order is what makes replay order
  // dependency-safe (see offlineSync.js), so this must never be left to
  // incidental store ordering.
  return items.sort((a, b) => a.id - b.id);
}

export async function getQueueLength() {
  const db = await getOfflineDb();
  return db.count("queue");
}

export async function updateQueueItem(id, patch) {
  const db = await getOfflineDb();
  const existing = await db.get("queue", id);
  if (!existing) return;
  await db.put("queue", { ...existing, ...patch });
}

export async function deleteQueueItem(id) {
  const db = await getOfflineDb();
  await db.delete("queue", id);
}

// markSyncComplete clears offline_since and stamps lastSyncedAt — called
// once the queue is fully drained (no "failed" items left behind either),
// so the offline-duration cap (see OfflineContext.js) resets for the NEXT
// offline stretch instead of accumulating across ones already synced.
export async function markSyncComplete() {
  const db = await getOfflineDb();
  await db.delete("meta", "offline_since");
  await db.put("meta", { key: "lastSyncedAt", value: new Date().toISOString() });
}

export async function getOfflineSince() {
  const db = await getOfflineDb();
  const row = await db.get("meta", "offline_since");
  return row ? row.value : null;
}

// --- cache --------------------------------------------------------------

// setCached/getCached back the reference-data fallback Checkout.js's own
// data-loading effects use (see this feature's own plan doc) — each
// successful ONLINE fetch writes its result here; each fetch's failure path
// (network error) reads it back instead of leaving the screen empty.
export async function setCached(key, data) {
  const db = await getOfflineDb();
  await db.put("cache", { key, data, cachedAt: new Date().toISOString() });
}

export async function getCached(key) {
  const db = await getOfflineDb();
  const row = await db.get("cache", key);
  return row ? row.data : null;
}
